import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Award, ExternalLink, ShieldCheck, Star } from "lucide-react";

/** Public confirmed-references block on a pro's profile.
 *  Referee identity is hidden — company name (with website backlink if provided),
 *  role held, period, and reliability rating are shown. */
export default function PublicReferences({ userId }) {
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    if (!userId) return;
    api.get(`/public/professionals/${userId}/references`)
       .then((r) => setRefs(r.data || []))
       .catch(() => setRefs([]));
  }, [userId]);

  if (!refs || refs.length === 0) return null;

  return (
    <div className="card-flat p-6" data-testid="public-references">
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-brand" />
        <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Confirmed employer references</div>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-white/50 font-outfit font-bold">{refs.length} on file</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {refs.map((r, i) => (
          <div key={i} className="rounded-md border border-white/10 bg-[#0E0E0E]/40 p-4" data-testid={`public-ref-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-outfit font-semibold text-white flex items-center gap-2">
                  {r.company_website ? (
                    <a href={r.company_website.startsWith("http") ? r.company_website : `https://${r.company_website}`} target="_blank" rel="noopener noreferrer nofollow" className="text-brand hover:underline flex items-center gap-1" data-testid={`public-ref-link-${i}`}>
                      {r.company_name} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  ) : (
                    <span>{r.company_name}</span>
                  )}
                </div>
                {(r.role_held || r.period) && (
                  <div className="text-xs text-white/60 font-manrope mt-0.5">
                    {r.role_held && <>{r.role_held}</>}{r.role_held && r.period && " · "}{r.period}
                  </div>
                )}
              </div>
              <ShieldCheck className="w-4 h-4 text-gold-500 flex-shrink-0" />
            </div>
            {r.reliability_rating && (
              <div className="mt-2 flex items-center gap-1">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.reliability_rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                ))}
                <span className="text-[11px] font-manrope text-white/60 ml-1">Referee-rated reliability</span>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-[11px] text-white/50 font-manrope mt-3 italic">Referee names remain private to SecuSource admin. Companies see only that a genuine employer confirmed the working relationship.</div>
    </div>
  );
}
